/**
 * DashboardSelector — dropdown in the top header bar for quick dashboard navigation.
 * Shows all dashboards the user has access to, grouped by category.
 * Uses TanStack Query for shared caching — multiple instances won't duplicate requests.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, ChevronDown, ChevronRight, Search, Bookmark, Pin } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { listDashboards, listFavorites, toggleFavorite, setDefaultDashboard } from '@/lib/bi/dashboard-api';
import type { DashboardFavorite } from '@/lib/bi/dashboard-api';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import './DashboardSelector.css';

export function DashboardSelector() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [favoritesCollapsed, setFavoritesCollapsed] = useState(false);
  const [pinCandidate, setPinCandidate] = useState<string | null>(null);
  const [pinning, setPinning] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useAuthStore((s) => s.user?.id);
  const envId = useAuthStore((s) => s.user?.active_environment_id);
  const queryClient = useQueryClient();

  // Shared query — cached across all DashboardSelector instances
  const { data: dashboards = [] } = useQuery({
    queryKey: ['dashboards', userId, envId],
    queryFn: listDashboards,
    staleTime: 60_000,
    enabled: !!userId && !!envId,
  });

  // Load favorites
  const { data: favorites = [] } = useQuery({
    queryKey: ['dashboard-favorites', userId, envId],
    queryFn: listFavorites,
    staleTime: 60_000,
    enabled: !!userId && !!envId,
  });

  const favoriteIds = useMemo(() => new Set(favorites.map((f: DashboardFavorite) => f.dashboard_id)), [favorites]);
  const defaultDashboardId = useMemo(
    () => favorites.find((f: DashboardFavorite) => f.is_default)?.dashboard_id ?? null,
    [favorites],
  );

  const handleToggleFavorite = async (e: React.MouseEvent, dashboardId: string) => {
    e.stopPropagation();
    try {
      await toggleFavorite(dashboardId);
      void queryClient.invalidateQueries({ queryKey: ['dashboard-favorites', userId, envId] });
    } catch {
      // Silently fail — non-critical action
    }
  };

  const handleToggleDefault = (e: React.MouseEvent, dashboardId: string) => {
    e.stopPropagation();
    if (defaultDashboardId === dashboardId) {
      void (async () => {
        try {
          await setDefaultDashboard(null);
          void queryClient.invalidateQueries({ queryKey: ['dashboard-favorites', userId, envId] });
        } catch {
          /* non-critical */
        }
      })();
      return;
    }
    if (defaultDashboardId) {
      setPinCandidate(dashboardId);
      return;
    }
    void (async () => {
      try {
        await setDefaultDashboard(dashboardId);
        void queryClient.invalidateQueries({ queryKey: ['dashboard-favorites', userId, envId] });
      } catch {
        /* non-critical */
      }
    })();
  };

  const confirmPin = async () => {
    if (!pinCandidate) {
      return;
    }
    setPinning(true);
    try {
      await setDefaultDashboard(pinCandidate);
      void queryClient.invalidateQueries({ queryKey: ['dashboard-favorites', userId, envId] });
      setPinCandidate(null);
    } catch {
      /* non-critical */
    } finally {
      setPinning(false);
    }
  };

  // Extract current dashboard ID from URL
  const currentDashboardId = useMemo(() => {
    const match = location.pathname.match(/^\/bi\/([^/?]+)/);
    return match?.[1] || null;
  }, [location.pathname]);

  const currentDashboard = useMemo(
    () => dashboards.find((d) => d.id === currentDashboardId),
    [dashboards, currentDashboardId],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) {
      return;
    }
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // When not searching: split favorites out; when searching: show everything together
  const { favoritedItems, grouped } = useMemo(() => {
    const term = search.toLowerCase();
    const filtered = term
      ? dashboards.filter(
          (d) => d.name.toLowerCase().includes(term) || (d.description || '').toLowerCase().includes(term),
        )
      : dashboards;

    // Favorites section — only when not searching
    const favs = !term
      ? [...filtered]
          .filter((d) => favoriteIds.has(d.id))
          .sort((a, b) => {
            if (a.id === defaultDashboardId) {
              return -1;
            }
            if (b.id === defaultDashboardId) {
              return 1;
            }
            return 0;
          })
      : [];
    const favIds = new Set(favs.map((d) => d.id));

    // Remaining dashboards — sorted, grouped by category
    const rest = !term ? filtered.filter((d) => !favIds.has(d.id)) : filtered;
    const sorted = [...rest].sort((a, b) => {
      const rank = (d: typeof a) => (d.id === defaultDashboardId ? 0 : favoriteIds.has(d.id) ? 1 : 2);
      return rank(a) - rank(b);
    });

    const groups = new Map<string, typeof dashboards>();
    for (const d of sorted) {
      const cat = d.category || '';
      if (!groups.has(cat)) {
        groups.set(cat, []);
      }
      groups.get(cat)!.push(d);
    }
    return { favoritedItems: favs, grouped: groups };
  }, [dashboards, search, defaultDashboardId, favoriteIds]);

  if (dashboards.length === 0) {
    return null;
  }

  return (
    <div className="dashboard-selector" ref={ref}>
      <button
        className={`dashboard-selector-btn ${currentDashboard ? 'active' : ''}`}
        onClick={() => {
          setOpen(!open);
          setSearch('');
        }}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <BarChart3 size={14} />
        <span className="dashboard-selector-label">{currentDashboard ? currentDashboard.name : 'Dashboards'}</span>
        <ChevronDown size={12} className={`dashboard-selector-chevron ${open ? 'open' : ''}`} />
      </button>

      {open && (
        <div className="dashboard-selector-dropdown" role="menu">
          {dashboards.length > 5 && (
            <div className="dashboard-selector-search">
              <Search size={12} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search dashboards..."
                aria-label="Search dashboards"
                autoFocus
              />
            </div>
          )}

          {/* All Dashboards link */}
          <button
            role="menuitem"
            className={`dashboard-selector-item ${location.pathname === '/bi' ? 'active' : ''}`}
            onClick={() => {
              void navigate('/bi');
              setOpen(false);
              setSearch('');
            }}
          >
            <span className="dashboard-selector-item-name">All Dashboards</span>
          </button>

          <div className="dashboard-selector-divider" />

          {/* Favorites section — only when not searching */}
          {favoritedItems.length > 0 && (
            <div className="dashboard-selector-favorites-group">
              <button
                className="dashboard-selector-section-header"
                onClick={() => setFavoritesCollapsed((v) => !v)}
                aria-expanded={!favoritesCollapsed}
              >
                {favoritesCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
                <Bookmark size={10} strokeLinecap="square" strokeLinejoin="miter" />
                <span>Favorites</span>
                <span className="dashboard-selector-section-count">{favoritedItems.length}</span>
              </button>
              {!favoritesCollapsed &&
                favoritedItems.map((d) => (
                  <div
                    key={d.id}
                    role="menuitem"
                    tabIndex={0}
                    className={`dashboard-selector-item ${d.id === currentDashboardId ? 'active' : ''}`}
                    onClick={() => {
                      void navigate(`/bi/${d.id}`);
                      setOpen(false);
                      setSearch('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void navigate(`/bi/${d.id}`);
                        setOpen(false);
                        setSearch('');
                      }
                    }}
                  >
                    <div className="dashboard-selector-item-row">
                      <span className="dashboard-selector-item-name">{d.name}</span>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <button
                          type="button"
                          className={`dashboard-selector-pin ${d.id === defaultDashboardId ? 'pinned' : ''}`}
                          onClick={(e) => {
                            void handleToggleDefault(e, d.id);
                          }}
                          aria-label={d.id === defaultDashboardId ? 'Unpin dashboard' : 'Pin Dashboard'}
                          aria-pressed={d.id === defaultDashboardId}
                          title={d.id === defaultDashboardId ? 'Unpin dashboard' : 'Pin Dashboard'}
                        >
                          <Pin size={11} fill={d.id === defaultDashboardId ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          type="button"
                          className="dashboard-selector-star starred"
                          onClick={(e) => {
                            void handleToggleFavorite(e, d.id);
                          }}
                          aria-label="Remove from favorites"
                          aria-pressed={true}
                          title="Remove from favorites"
                        >
                          <Bookmark size={12} strokeLinecap="square" strokeLinejoin="miter" fill="currentColor" />
                        </button>
                      </div>
                    </div>
                    {d.description && (
                      <span className="dashboard-selector-item-desc">
                        {d.description.length > 50 ? d.description.slice(0, 48) + '...' : d.description}
                      </span>
                    )}
                  </div>
                ))}
              <div className="dashboard-selector-divider" />
            </div>
          )}

          {/* Remaining dashboards grouped by category */}
          {[...grouped.entries()].map(([category, items]) => (
            <div key={category || '__uncategorized'}>
              {category && <div className="dashboard-selector-category">{category}</div>}
              {items.map((d) => (
                <div
                  key={d.id}
                  role="menuitem"
                  tabIndex={0}
                  className={`dashboard-selector-item ${d.id === currentDashboardId ? 'active' : ''}`}
                  onClick={() => {
                    void navigate(`/bi/${d.id}`);
                    setOpen(false);
                    setSearch('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void navigate(`/bi/${d.id}`);
                      setOpen(false);
                      setSearch('');
                    }
                  }}
                >
                  <div className="dashboard-selector-item-row">
                    <span className="dashboard-selector-item-name">{d.name}</span>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <button
                        type="button"
                        className={`dashboard-selector-pin ${d.id === defaultDashboardId ? 'pinned' : ''}`}
                        onClick={(e) => {
                          void handleToggleDefault(e, d.id);
                        }}
                        aria-label={d.id === defaultDashboardId ? 'Unpin dashboard' : 'Pin Dashboard'}
                        aria-pressed={d.id === defaultDashboardId}
                        title={d.id === defaultDashboardId ? 'Unpin dashboard' : 'Pin Dashboard'}
                      >
                        <Pin size={11} fill={d.id === defaultDashboardId ? 'currentColor' : 'none'} />
                      </button>
                      <button
                        type="button"
                        className={`dashboard-selector-star ${favoriteIds.has(d.id) ? 'starred' : ''}`}
                        onClick={(e) => {
                          void handleToggleFavorite(e, d.id);
                        }}
                        aria-label={favoriteIds.has(d.id) ? 'Remove from favorites' : 'Add to favorites'}
                        aria-pressed={favoriteIds.has(d.id)}
                        title={favoriteIds.has(d.id) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Bookmark
                          size={12}
                          strokeLinecap="square"
                          strokeLinejoin="miter"
                          fill={favoriteIds.has(d.id) ? 'currentColor' : 'none'}
                        />
                      </button>
                    </div>
                  </div>
                  {d.description && (
                    <span className="dashboard-selector-item-desc">
                      {d.description.length > 50 ? d.description.slice(0, 48) + '...' : d.description}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}

          {grouped.size === 0 && favoritedItems.length === 0 && search && (
            <div className="dashboard-selector-empty">No dashboards match &quot;{search}&quot;</div>
          )}
        </div>
      )}
      <ConfirmModal
        open={!!pinCandidate}
        title="Replace pinned dashboard?"
        message={`"${dashboards.find((d) => d.id === defaultDashboardId)?.name}" is currently pinned. Replace it with "${dashboards.find((d) => d.id === pinCandidate)?.name}"?`}
        confirmLabel="Pin Dashboard"
        confirmVariant="primary"
        loading={pinning}
        onConfirm={() => {
          void confirmPin();
        }}
        onCancel={() => setPinCandidate(null)}
      />
    </div>
  );
}
